import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Copy, Cylinder, ArrowLeftRight, ListPlus, LogOut, Monitor, MoveHorizontal, MoveVertical, Plus, RotateCcw, ShieldCheck, Smartphone, Table2, Trash2 } from "lucide-react";
import { TankDiagram } from "@/components/TankDiagram";
import { CalibrationGauge } from "@/components/CalibrationGauge";
import { CompartmentDiagram } from "@/components/CompartmentDiagram";
import { StrappingTable } from "@/components/StrappingTable";
import { SavedConfigs } from "@/components/SavedConfigs";
import { MeasurementLog } from "@/components/MeasurementLog";
import { AdminPanel } from "@/components/AdminPanel";
import { useAuth } from "@/context/AuthContext";
import { Toaster, toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LIQUIDS, LITER_CONVERSIONS, PRESETS, UNIT_TO_CM, capacityCm3, cleanCalibration, filledCrossArea, getGeometry, interpolateLevel, interpolateVolume, liquidCm3 } from "@/lib/tankMath";

const DEFAULTS = {
  orientation: "vertical",
  shape: "circular",
  unit: "cm",
  diameter: "100",
  width: "200",
  height: "140",
  tankLength: "128",
  fill: "96",
  liquidName: "B50",
  customDensity: "1.0",
  mixA: "B50",
  mixB: "Avtur",
  mixPct: "50",
  calibData: [
    { level: "0", liters: "0" },
    { level: "50", liters: "480" },
    { level: "100", liters: "1000" },
    { level: "150", liters: "1560" },
  ],
  calibReadMode: "level",
  calibLevel: "75",
  calibVolume: "740",
};

const hexLerp = (c1, c2, t) => {
  const ch = (h, i) => parseInt(h.slice(i, i + 2), 16);
  const mix = (a, b) => Math.round(a + (b - a) * t);
  return `#${[1, 3, 5].map((i) => mix(ch(c1, i), ch(c2, i)).toString(16).padStart(2, "0")).join("")}`;
};

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const num = (s) => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n, d = 2) => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d });

const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono";
const inputCls = "bg-[#0D131E] border-[#243046] font-mono text-slate-100 focus-visible:ring-amber-500";

const SegButton = ({ testid, active, onClick, icon, title, sub }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${
      active
        ? "border-amber-500 bg-amber-500/10 text-amber-300"
        : "border-[#243046] bg-[#0D131E] text-slate-400 hover:border-[#3B4B63] hover:text-slate-200"
    }`}
  >
    {icon}
    <span>
      <span className="block text-sm font-semibold font-mono">{title}</span>
      {sub && <span className="block text-[11px] opacity-70">{sub}</span>}
    </span>
  </button>
);

const UnitField = ({ label, unit, children }) => (
  <div>
    <p className="text-[11px] font-mono text-slate-500 mb-1">{label}</p>
    <div className="relative">
      {children}
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-500 pointer-events-none">{unit}</span>
    </div>
  </div>
);

const MetricCard = ({ label, value, unitLabel, testid, big, span = "" }) => (
  <div className={`bg-[#131924] rounded-xl border border-[#243046] p-5 shadow-xl ${span}`}>
    <p className={labelCls}>{label}</p>
    <p className={`mt-2 font-black font-mono tracking-tight ${big ? "text-3xl sm:text-4xl text-amber-400" : "text-2xl text-slate-100"}`}>
      <span data-testid={testid}>{value}</span>{" "}
      <span className="text-sm font-semibold text-slate-500">{unitLabel}</span>
    </p>
  </div>
);

const shapeIcons = {
  circular: (
    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
  ),
  elliptical: (
    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><ellipse cx="10" cy="10" rx="9" ry="6" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
  ),
  rectangular: (
    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><rect x="3" y="5" width="14" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
  ),
  stadium: (
    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><rect x="2" y="6" width="16" height="8" rx="4" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
  ),
};

function App() {
  const { user, logout } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [orientation, setOrientation] = useState(DEFAULTS.orientation);
  const [shape, setShape] = useState(DEFAULTS.shape);
  const [unit, setUnit] = useState(DEFAULTS.unit);
  const [diameter, setDiameter] = useState(DEFAULTS.diameter);
  const [width, setWidth] = useState(DEFAULTS.width);
  const [height, setHeight] = useState(DEFAULTS.height);
  const [tankLength, setTankLength] = useState(DEFAULTS.tankLength);
  const [fill, setFill] = useState(DEFAULTS.fill);
  const [liquidName, setLiquidName] = useState(DEFAULTS.liquidName);
  const [customDensity, setCustomDensity] = useState(DEFAULTS.customDensity);
  const [mixA, setMixA] = useState(DEFAULTS.mixA);
  const [mixB, setMixB] = useState(DEFAULTS.mixB);
  const [mixPct, setMixPct] = useState(DEFAULTS.mixPct);
  const [calibData, setCalibData] = useState(DEFAULTS.calibData);
  const [calibReadMode, setCalibReadMode] = useState(DEFAULTS.calibReadMode);
  const [calibLevel, setCalibLevel] = useState(DEFAULTS.calibLevel);
  const [calibVolume, setCalibVolume] = useState(DEFAULTS.calibVolume);
  const [compEnabled, setCompEnabled] = useState(false);
  const [compCount, setCompCount] = useState(2);
  const [comps, setComps] = useState(() =>
    Array.from({ length: 3 }, () => ({ fill: "0", liquid: "B50" }))
  );
  const [tankName, setTankName] = useState("");
  const [logRows, setLogRows] = useState([]);
  const [logHighlightId, setLogHighlightId] = useState(null);
  const isAdmin = user?.role === "admin";
  const [strapOpen, setStrapOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("tankflow.viewMode") || (window.innerWidth < 1024 ? "mobile" : "desktop");
    } catch {
      return "desktop";
    }
  });

  useEffect(() => {
    document.title = "Tank Volume Calculator";
  }, []);

  const isMobileView = viewMode === "mobile";
  const toggleViewMode = () => {
    const next = isMobileView ? "desktop" : "mobile";
    setViewMode(next);
    try {
      localStorage.setItem("tankflow.viewMode", next);
    } catch (err) {
      console.warn("Could not persist view mode preference:", err);
    }
    toast.success(next === "mobile" ? "Mobile view enabled" : "Desktop view enabled");
  };

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const defaultLiquid = LIQUIDS.find((l) => l.name === DEFAULTS.liquidName) || LIQUIDS[0];
  const liquid = LIQUIDS.find((l) => l.name === liquidName) || defaultLiquid;
  const mixables = LIQUIDS.filter((l) => l.name !== "Mixture" && l.name !== "Custom");
  const mixALiq = mixables.find((l) => l.name === mixA) || mixables.find((l) => l.name === DEFAULTS.mixA) || mixables[0];
  const mixBLiq = mixables.find((l) => l.name === mixB) || mixables.find((l) => l.name === DEFAULTS.mixB) || mixables[1];
  const pctClamped = clamp(num(mixPct), 0, 100);
  const mixFrac = pctClamped / 100;
  const isMixture = liquidName === "Mixture";
  const mixtureDensity = mixFrac * mixALiq.density + (1 - mixFrac) * mixBLiq.density;
  const density = liquidName === "Custom" ? num(customDensity) || 1 : isMixture ? mixtureDensity : liquid.density;
  const liquidColor = isMixture ? hexLerp(mixALiq.color, mixBLiq.color, mixFrac) : liquid.color;

  const geom = useMemo(
    () => getGeometry({ orientation, shape, diameter, width, height, tankLength, unit }),
    [orientation, shape, diameter, width, height, tankLength, unit]
  );

  const isCalibration = shape === "calibration";
  const calibPoints = useMemo(() => cleanCalibration(calibData), [calibData]);
  const calibCapacityL = calibPoints.length ? calibPoints[calibPoints.length - 1].liters : 0;
  const calibMinLevel = calibPoints.length ? calibPoints[0].level : 0;
  const calibMaxLevel = calibPoints.length ? calibPoints[calibPoints.length - 1].level : 0;
  const calibLevelVal = calibReadMode === "level" ? num(calibLevel) : interpolateLevel(calibPoints, clamp(num(calibVolume), 0, calibCapacityL));
  const calibLiqL = calibReadMode === "level" ? interpolateVolume(calibPoints, calibLevelVal) : clamp(num(calibVolume), 0, calibCapacityL);
  const calibFrac = calibMaxLevel > calibMinLevel ? clamp((calibLevelVal - calibMinLevel) / (calibMaxLevel - calibMinLevel), 0, 1) : 0;

  const fillCm = geom ? clamp(num(fill) * UNIT_TO_CM[unit], 0, geom.maxFillCm) : 0;

  const isCompartments = compEnabled && (orientation === "horizontal" || isCalibration);
  const compStats = useMemo(() => {
    if (!isCompartments) return [];
    if (isCalibration) {
      if (calibPoints.length < 2) return [];
      return comps.slice(0, compCount).map((c) => {
        const lvl = num(c.fill);
        const liters = interpolateVolume(calibPoints, lvl);
        const liq = LIQUIDS.find((l) => l.name === c.liquid) || defaultLiquid;
        const frac = calibMaxLevel > calibMinLevel ? clamp((lvl - calibMinLevel) / (calibMaxLevel - calibMinLevel), 0, 1) : 0;
        return { fill: c.fill, liquid: c.liquid, frac, liters, weight: liters * liq.density, color: liq.color };
      });
    }
    if (!geom) return [];
    const compLenCm = geom.lengthCm / compCount;
    return comps.slice(0, compCount).map((c) => {
      const fCm = clamp(num(c.fill) * UNIT_TO_CM[unit], 0, geom.maxFillCm);
      const liters = (filledCrossArea(shape, geom.wCm, geom.hCm, fCm) * compLenCm) / 1000;
      const liq = LIQUIDS.find((l) => l.name === c.liquid) || defaultLiquid;
      return {
        fill: c.fill,
        liquid: c.liquid,
        frac: geom.maxFillCm > 0 ? fCm / geom.maxFillCm : 0,
        liters,
        weight: liters * liq.density,
        color: liq.color,
      };
    });
  }, [isCompartments, isCalibration, calibPoints, calibMinLevel, calibMaxLevel, geom, compCount, comps, unit, shape]);
  const compLiqL = compStats.reduce((s, c) => s + c.liters, 0);
  const compWeightKg = compStats.reduce((s, c) => s + c.weight, 0);

  const capL = isCalibration ? (isCompartments ? calibCapacityL * compCount : calibCapacityL) : geom ? capacityCm3(geom) / 1000 : 0;
  const liqL = isCompartments ? compLiqL : isCalibration ? calibLiqL : geom ? liquidCm3(geom, fillCm) / 1000 : 0;
  const fillPct = capL > 0 ? (liqL / capL) * 100 : 0;
  const ullageL = Math.max(0, capL - liqL);
  const weightKg = isCompartments ? compWeightKg : liqL * density;
  const overflow = isCompartments ? compStats.some((c) => c.frac >= 0.95) : fillPct >= 95;
  const frac = isCalibration ? calibFrac : geom && geom.maxFillCm > 0 ? clamp(fillCm / geom.maxFillCm, 0, 1) : 0;
  const fillExceeds = geom ? num(fill) * UNIT_TO_CM[unit] > geom.maxFillCm : false;

  const strapRows = useMemo(() => {
    if (isCalibration) {
      if (calibPoints.length < 2 || calibCapacityL <= 0) return [];
      const rows = [];
      for (let p = 5; p <= 100; p += 5) {
        const liters = (calibCapacityL * p) / 100;
        rows.push({ pct: p, height: interpolateLevel(calibPoints, liters), liters });
      }
      return rows;
    }
    if (!geom) return [];
    const rows = [];
    for (let p = 5; p <= 100; p += 5) {
      const hCm = (geom.maxFillCm * p) / 100;
      rows.push({ pct: p, height: hCm / UNIT_TO_CM[unit], liters: liquidCm3(geom, hCm) / 1000 });
    }
    return rows;
  }, [geom, unit, isCalibration, calibPoints, calibCapacityL]);

  const handleUnitChange = (next) => {
    const f = UNIT_TO_CM[unit] / UNIT_TO_CM[next];
    const conv = (s) => {
      const n = parseFloat(s);
      return Number.isFinite(n) ? String(+(n * f).toFixed(4)) : s;
    };
    setDiameter(conv(diameter));
    setWidth(conv(width));
    setHeight(conv(height));
    setTankLength(conv(tankLength));
    setFill(conv(fill));
    setUnit(next);
  };

  const applyPreset = (p) => {
    setOrientation(p.orientation);
    setShape(p.shape);
    setUnit(p.unit);
    if (p.diameter) setDiameter(p.diameter);
    if (p.width) setWidth(p.width);
    if (p.height) setHeight(p.height);
    setTankLength(p.tankLength);
    setFill(p.fill);
    setLiquidName(p.liquid);
    toast.success(`Preset applied: ${p.name}`);
  };

  const handleReset = () => {
    setOrientation(DEFAULTS.orientation);
    setShape(DEFAULTS.shape);
    setUnit(DEFAULTS.unit);
    setDiameter(DEFAULTS.diameter);
    setWidth(DEFAULTS.width);
    setHeight(DEFAULTS.height);
    setTankLength(DEFAULTS.tankLength);
    setFill(DEFAULTS.fill);
    setLiquidName(DEFAULTS.liquidName);
    setCustomDensity(DEFAULTS.customDensity);
    setMixA(DEFAULTS.mixA);
    setMixB(DEFAULTS.mixB);
    setMixPct(DEFAULTS.mixPct);
    setCalibData(DEFAULTS.calibData);
    setCalibReadMode(DEFAULTS.calibReadMode);
    setCalibLevel(DEFAULTS.calibLevel);
    setCalibVolume(DEFAULTS.calibVolume);
    setCompEnabled(false);
    setCompCount(2);
    setComps(Array.from({ length: 3 }, () => ({ fill: "0", liquid: "B50" })));
    toast.success("Calculator reset to defaults");
  };

  const handleResizeDim = (key, valueCm) => {
    const v = String(+(valueCm / UNIT_TO_CM[unit]).toFixed(3));
    if (key === "diameter") setDiameter(v);
    else if (key === "width") setWidth(v);
    else if (key === "height") setHeight(v);
    else if (key === "length") setTankLength(v);
  };

  const getSnapshot = () => ({ orientation, shape, unit, diameter, width, height, tankLength, fill, liquidName, customDensity, mixA, mixB, mixPct, calibData, calibReadMode, calibLevel, calibVolume, compEnabled, compCount, comps });

  const applyConfig = (c, name) => {
    if (name) setTankName(name);
    setOrientation(c.orientation);
    setShape(c.shape);
    setUnit(c.unit);
    setDiameter(c.diameter);
    setWidth(c.width);
    setHeight(c.height);
    setTankLength(c.tankLength);
    setFill(c.fill);
    setLiquidName(LIQUIDS.some((l) => l.name === c.liquidName) ? c.liquidName : DEFAULTS.liquidName);
    setCustomDensity(c.customDensity);
    setMixA(mixables.some((l) => l.name === c.mixA) ? c.mixA : DEFAULTS.mixA);
    setMixB(mixables.some((l) => l.name === c.mixB) ? c.mixB : DEFAULTS.mixB);
    setMixPct(c.mixPct || DEFAULTS.mixPct);
    setCalibData(Array.isArray(c.calibData) && c.calibData.length ? c.calibData : DEFAULTS.calibData);
    setCalibReadMode(c.calibReadMode || DEFAULTS.calibReadMode);
    setCalibLevel(c.calibLevel || DEFAULTS.calibLevel);
    setCalibVolume(c.calibVolume || DEFAULTS.calibVolume);
    setCompEnabled(Boolean(c.compEnabled));
    setCompCount(clamp(parseInt(c.compCount, 10) || 2, 2, 3));
    setComps(
      Array.isArray(c.comps) && c.comps.length
        ? c.comps.map((comp) => ({ ...comp, liquid: LIQUIDS.some((l) => l.name === comp.liquid) ? comp.liquid : DEFAULTS.liquidName }))
        : Array.from({ length: 3 }, () => ({ fill: "0", liquid: DEFAULTS.liquidName }))
    );
  };

  const updateCalibRow = (i, key, value) => setCalibData((rows) => rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const addCalibRow = () => setCalibData((rows) => [...rows, { level: "", liters: "" }]);
  const removeCalibRow = (i) => setCalibData((rows) => rows.filter((_, j) => j !== i));

  const updateComp = (i, key, value) => setComps((rows) => rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)));

  const currentFillCmList = isCompartments
    ? comps.slice(0, compCount).map((c) => +(num(c.fill) * (isCalibration ? 1 : UNIT_TO_CM[unit])).toFixed(2))
    : isCalibration
      ? [+calibLevelVal.toFixed(2)]
      : [+(num(fill) * UNIT_TO_CM[unit]).toFixed(2)];

  const currentProducts = isCompartments ? comps.slice(0, compCount).map((c) => c.liquid) : [liquid.name];

  const addLogRow = () => {
    const name = tankName.trim() || `Tank ${logRows.length + 1}`;
    const dup = logRows.find((r) => r.name.trim().toLowerCase() === name.toLowerCase());
    if (dup) {
      setLogHighlightId(dup.id);
      setTimeout(() => setLogHighlightId(null), 2000);
      toast.error(`"${dup.name}" is already in the log — remove it first or use a different name`);
      return;
    }
    setLogRows((rows) => [...rows, { id: Date.now(), name, fillCm: currentFillCmList, products: currentProducts, volume: liqL, weight: weightKg }]);
    toast.success("Reading added to measurement log");
  };

  const deleteLogRow = (id) => {
    setLogRows((rows) => rows.filter((r) => r.id !== id));
    toast.success("Row removed");
  };

  const handleCopy = async () => {
    const lines = [
      "TankFlow — Tank Liquid Volume",
      `Tank: ${orientation === "vertical" ? "Vertical" : "Horizontal"} / ${shape}${isCompartments ? ` (${compCount} compartments)` : ""}`,
      `Dimensions: ${shape === "circular" ? `Ø ${diameter} ${unit}` : `${width} × ${height} ${unit}`} · ${orientation === "vertical" ? "H" : "L"} ${tankLength} ${unit}`,
      `Fill Level: ${fill} ${unit} (${fmt(fillPct, 1)}%)`,
      `Liquid Volume: ${fmt(liqL)} L`,
      `Total Capacity: ${fmt(capL)} L`,
      `Ullage: ${fmt(ullageL)} L`,
    ];
    lines.push(
      isCompartments
        ? "Liquid: Per-compartment products"
        : `Liquid: ${isMixture ? `Mixture (${pctClamped}% ${mixALiq.name} + ${100 - pctClamped}% ${mixBLiq.name})` : liquidName} (${fmt(density, 3)} kg/L) · Weight: ${fmt(weightKg, 1)} kg`
    );
    if (isCompartments && compStats.length) {
      lines.push("Compartments:");
      compStats.forEach((c, i) => lines.push(`  C${i + 1}: ${c.fill} ${unit} · ${c.liquid} · ${fmt(c.liters)} L · ${fmt(c.weight, 1)} kg`));
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Summary copied to clipboard");
    } catch (err) {
      console.warn("Clipboard write failed:", err);
      toast.error("Couldn't access clipboard — copy blocked by your browser");
    }
  };

  const dimLabels = (() => {
    if (shape === "rectangular") return orientation === "vertical" ? ["Base Width", "Base Depth"] : ["Width", "Height"];
    if (shape === "stadium") return orientation === "vertical" ? ["Base Length", "Base Width (end Ø)"] : ["Overall Width", "Height (end Ø)"];
    return orientation === "vertical" ? ["Cross Width", "Cross Depth"] : ["Width", "Height"];
  })();

  const dimsText = shape === "circular" ? `Ø ${diameter} ${unit}` : `${width} × ${height} ${unit}`;
  const labels = {
    crossW: orientation === "vertical" ? dimsText : shape === "circular" ? `Ø ${diameter} ${unit}` : `W ${width} ${unit}`,
    sideW: shape === "circular" ? `Ø ${diameter} ${unit}` : `W ${width} ${unit}`,
    crossH: shape !== "circular" && orientation === "horizontal" ? `H ${height} ${unit}` : null,
    len: `${orientation === "vertical" ? "H" : "L"} ${tankLength} ${unit}`,
    fill: `${fill} ${unit} · ${fmt(fillPct, 0)}%`,
  };
  const meta = isCalibration
    ? `Custom calibration tank — ${calibPoints.length} points, capacity ${fmt(calibCapacityL)} L`
    : `${orientation === "vertical" ? "Vertical" : "Horizontal"} ${shape} tank — ${
        shape === "circular" ? `Ø${diameter}` : `${width}×${height}`
      } ${unit}, ${orientation === "vertical" ? "H" : "L"} ${tankLength} ${unit}`;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200 bg-grid">
      <Toaster position="top-right" richColors />
      <header data-testid="app-header" className="border-b border-[#243046] bg-[#0D131E]/90">
        <div className={`${isMobileView ? "max-w-[480px]" : "max-w-7xl"} mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center gap-x-4 gap-y-3`}>
          <div className="h-11 w-11 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-center justify-center shrink-0">
            <Cylinder className="h-6 w-6 text-amber-400" />
          </div>
          <div className="min-w-0">
            <h1 className={`${isMobileView ? "text-base" : "text-2xl sm:text-3xl"} font-extrabold tracking-tight font-mono text-slate-100 leading-tight`}>TANK VOLUME CALCULATOR</h1>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2 shrink-0">
            {!isOnline && (
              <span data-testid="offline-status-pill" className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-[10px] font-mono font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" />
                OFFLINE
              </span>
            )}
            <button
              data-testid="view-mode-toggle"
              onClick={toggleViewMode}
              title={isMobileView ? "Switch to desktop view" : "Switch to mobile view"}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#243046] bg-[#0D131E] text-slate-300 hover:border-amber-500/60 hover:text-amber-300 transition-colors"
            >
              {isMobileView ? <Smartphone className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
              <span className="hidden sm:inline text-xs font-mono">{isMobileView ? "Mobile" : "Desktop"}</span>
            </button>
            {user?.role === "admin" && (
              <button
                data-testid="admin-panel-btn"
                onClick={() => setAdminOpen(true)}
                title="Manage users"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
              >
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline text-xs font-mono">Users</span>
              </button>
            )}
            <div className="flex items-center gap-2 pl-1">
              <span className="hidden lg:block text-right leading-tight">
                <span className="block text-xs font-mono text-slate-200 max-w-[160px] truncate" data-testid="current-user-email">{user?.email}</span>
                <span className="block text-[10px] font-mono text-slate-500">{user?.role}</span>
              </span>
              <button
                data-testid="logout-btn"
                onClick={logout}
                title="Sign out"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#243046] bg-[#0D131E] text-slate-300 hover:border-red-500/60 hover:text-red-300 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline text-xs font-mono">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className={`${isMobileView ? "max-w-[480px] border-x border-[#243046]" : "max-w-7xl"} mx-auto px-4 sm:px-6 lg:px-8 py-8`}>
        <div className={isMobileView ? "grid grid-cols-1 gap-8 items-start" : "grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"}>
          {/* Input panel */}
          <section className={`${isMobileView ? "" : "lg:col-span-5 "}space-y-6 bg-[#131924] p-6 sm:p-8 rounded-xl border border-[#243046] shadow-2xl rise`} data-testid="input-panel">
            <div>
              <p className={labelCls}>Quick Presets</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {PRESETS.map((p, i) => (
                  <button
                    key={p.name}
                    data-testid={`preset-btn-${i}`}
                    onClick={() => applyPreset(p)}
                    className="text-left px-3 py-2 rounded-lg border border-[#243046] bg-[#0D131E] text-xs text-slate-300 hover:border-amber-500/60 hover:text-amber-300 transition-colors"
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>

            <SavedConfigs getSnapshot={getSnapshot} onLoad={applyConfig} isAdmin={isAdmin} isMobileView={isMobileView} />

            {!isCalibration && (
              <div>
                <p className={labelCls}>Tank Orientation</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <SegButton testid="orientation-vertical-btn" active={orientation === "vertical"} onClick={() => setOrientation("vertical")} icon={<MoveVertical className="h-4 w-4 shrink-0" />} title="Vertical" sub="Standing tank" />
                  <SegButton testid="orientation-horizontal-btn" active={orientation === "horizontal"} onClick={() => setOrientation("horizontal")} icon={<MoveHorizontal className="h-4 w-4 shrink-0" />} title="Horizontal" sub="Lying vessel" />
                </div>
              </div>
            )}

            <div>
              <p className={labelCls}>Base / Cross-Section Shape</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <SegButton testid="shape-circular-btn" active={shape === "circular"} onClick={() => setShape("circular")} icon={shapeIcons.circular} title="Circular" sub="Diameter" />
                <SegButton testid="shape-elliptical-btn" active={shape === "elliptical"} onClick={() => setShape("elliptical")} icon={shapeIcons.elliptical} title="Elliptical" sub="Width × height" />
                <SegButton testid="shape-rectangular-btn" active={shape === "rectangular"} onClick={() => setShape("rectangular")} icon={shapeIcons.rectangular} title="Rectangular" sub="Width × depth" />
                <SegButton testid="shape-stadium-btn" active={shape === "stadium"} onClick={() => setShape("stadium")} icon={shapeIcons.stadium} title="Stadium" sub="Capsule ends" />
              </div>
              <button
                data-testid="shape-calibration-btn"
                onClick={() => setShape("calibration")}
                className={`mt-2 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                  isCalibration ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-[#243046] bg-[#0D131E] text-slate-400 hover:border-[#3B4B63] hover:text-slate-200"
                }`}
              >
                <ListPlus className="h-4 w-4 shrink-0" />
                <span>
                  <span className="block text-sm font-semibold font-mono">Custom Calibration</span>
                  <span className="block text-[11px] opacity-70">Volume from calibration data (linearised)</span>
                </span>
              </button>
            </div>

            {(orientation === "horizontal" || isCalibration) && (
              <div data-testid="compartments-section">
                <p className={labelCls}>Compartments</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <SegButton testid="comp-single-btn" active={!compEnabled} onClick={() => setCompEnabled(false)} icon={<MoveHorizontal className="h-4 w-4 shrink-0" />} title="Single" sub="One chamber" />
                  <SegButton testid="comp-multi-btn" active={compEnabled} onClick={() => setCompEnabled(true)} icon={<ListPlus className="h-4 w-4 shrink-0" />} title="Multi" sub="2–3 compartments" />
                </div>
                {compEnabled && (
                  <>
                    <div className="flex items-center gap-1.5 mt-3">
                      <span className="text-[11px] font-mono text-slate-500 mr-1">Count:</span>
                      {[2, 3].map((n) => (
                        <button
                          key={n}
                          data-testid={`comp-count-${n}`}
                          onClick={() => setCompCount(n)}
                          className={`h-7 w-7 rounded-md text-xs font-mono border transition-colors ${
                            compCount === n ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-[#243046] bg-[#0D131E] text-slate-400 hover:border-[#3B4B63]"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 space-y-2" data-testid="compartments-list">
                      {Array.from({ length: compCount }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2 rounded-lg border border-[#243046] bg-[#0D131E] p-2" data-testid={`comp-row-${i}`}>
                          <span className="text-[10px] font-mono font-semibold px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 shrink-0">C{i + 1}</span>
                          <div className="relative flex-1 min-w-0">
                            <Input
                              data-testid={`comp-fill-${i}`}
                              type="number"
                              min="0"
                              value={comps[i].fill}
                              onChange={(e) => updateComp(i, "fill", e.target.value)}
                              className="bg-[#131924] border-[#243046] font-mono text-slate-100 text-xs h-8"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-500 pointer-events-none">{isCalibration ? "cm" : unit}</span>
                          </div>
                          <Select value={comps[i].liquid} onValueChange={(v) => updateComp(i, "liquid", v)}>
                            <SelectTrigger data-testid={`comp-liquid-${i}`} className="w-[130px] h-8 bg-[#131924] border-[#243046] font-mono text-slate-200 text-xs shrink-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#131924] border-[#243046] text-slate-200">
                              {LIQUIDS.filter((l) => l.name !== "Mixture" && l.name !== "Custom").map((l) => (
                                <SelectItem key={l.name} value={l.name} data-testid={`comp-liquid-option-${i}-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                  {l.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {isCalibration ? (
              <div data-testid="calibration-panel">
                <div className="flex items-center justify-between">
                  <p className={labelCls}>Calibration Table {!isAdmin && <span className="text-slate-600 normal-case">(admin-managed)</span>}</p>
                  <span className="text-[10px] font-mono text-slate-500">level (cm) → volume (L)</span>
                </div>
                <div className="mt-2 rounded-lg border border-[#243046] overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-px bg-[#243046] text-[10px] font-mono uppercase tracking-wider text-slate-400 sticky top-0 z-10">
                      <span className="bg-[#0D131E] px-2 sm:px-3 py-1.5">Fill Level (cm)</span>
                      <span className="bg-[#0D131E] px-2 sm:px-3 py-1.5">Volume (L)</span>
                      <span className="bg-[#0D131E] px-2 py-1.5 w-9" />
                    </div>
                    {calibData.map((row, i) => (
                    <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-px bg-[#243046]" data-testid={`calib-row-${i}`}>
                      <input
                        data-testid={`calib-level-${i}`}
                        type="number"
                        value={row.level}
                        disabled={!isAdmin}
                        onChange={(e) => updateCalibRow(i, "level", e.target.value)}
                        className="w-full min-w-0 bg-[#0D131E] px-2 sm:px-3 py-1.5 text-sm font-mono text-slate-100 outline-none focus:bg-[#131924] disabled:text-slate-400 disabled:cursor-not-allowed"
                      />
                      <input
                        data-testid={`calib-liters-${i}`}
                        type="number"
                        value={row.liters}
                        disabled={!isAdmin}
                        onChange={(e) => updateCalibRow(i, "liters", e.target.value)}
                        className="w-full min-w-0 bg-[#0D131E] px-2 sm:px-3 py-1.5 text-sm font-mono text-slate-100 outline-none focus:bg-[#131924] disabled:text-slate-400 disabled:cursor-not-allowed"
                      />
                      {isAdmin ? (
                        <button data-testid={`calib-remove-${i}`} onClick={() => removeCalibRow(i)} className="bg-[#0D131E] px-2 w-9 flex items-center justify-center text-slate-500 hover:text-red-400" title="Remove row">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <span className="bg-[#0D131E] w-9" />
                      )}
                    </div>
                    ))}
                  </div>
                </div>
                {isAdmin && (
                  <button data-testid="calib-add-row-btn" onClick={addCalibRow} className="mt-2 flex items-center gap-1.5 text-xs font-mono text-amber-300 hover:text-amber-200">
                    <Plus className="h-3.5 w-3.5" />Add calibration point
                  </button>
                )}
                {calibPoints.length < 2 && (
                  <p className="text-[11px] font-mono text-red-400 mt-2" data-testid="calib-warning">Add at least 2 valid calibration points to compute volume.</p>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <p className={labelCls}>Dimensions</p>
                  <Select value={unit} onValueChange={handleUnitChange}>
                    <SelectTrigger data-testid="unit-selector" className="w-24 h-8 bg-[#0D131E] border-[#243046] text-xs font-mono text-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131924] border-[#243046] text-slate-200">
                      {Object.keys(UNIT_TO_CM).map((u) => (
                        <SelectItem key={u} value={u} data-testid={`unit-option-${u}`}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {shape === "circular" ? (
                    <UnitField label="Diameter (Ø)" unit={unit}>
                      <Input data-testid="diameter-input" type="number" min="0" value={diameter} onChange={(e) => setDiameter(e.target.value)} className={inputCls} />
                    </UnitField>
                  ) : (
                    <>
                      <UnitField label={dimLabels[0]} unit={unit}>
                        <Input data-testid="width-input" type="number" min="0" value={width} onChange={(e) => setWidth(e.target.value)} className={inputCls} />
                      </UnitField>
                      <UnitField label={dimLabels[1]} unit={unit}>
                        <Input data-testid="height-input" type="number" min="0" value={height} onChange={(e) => setHeight(e.target.value)} className={inputCls} />
                      </UnitField>
                    </>
                  )}
                  <UnitField label={orientation === "vertical" ? "Tank Height" : "Tank Length"} unit={unit}>
                    <Input data-testid="length-input" type="number" min="0" value={tankLength} onChange={(e) => setTankLength(e.target.value)} className={inputCls} />
                  </UnitField>
                </div>
                {shape === "stadium" && (
                  <p className="text-[11px] font-mono text-slate-500 mt-2" data-testid="stadium-hint">Semicircular ends — the long axis is auto-detected from your inputs.</p>
                )}
              </div>
            )}

            {isCalibration ? (
              isCompartments ? null : (
              <div data-testid="calibration-read-panel">
                <div className="flex items-center justify-between">
                  <p className={labelCls}>Read Value</p>
                  <button
                    data-testid="calib-mode-toggle"
                    onClick={() => setCalibReadMode((m) => (m === "level" ? "volume" : "level"))}
                    className="flex items-center gap-1.5 text-[11px] font-mono text-slate-300 px-2 py-1 rounded border border-[#243046] bg-[#0D131E] hover:border-amber-500/60 hover:text-amber-300"
                  >
                    <ArrowLeftRight className="h-3 w-3" />
                    {calibReadMode === "level" ? "Level → Volume" : "Volume → Level"}
                  </button>
                </div>
                {calibReadMode === "level" ? (
                  <div className="relative mt-2">
                    <p className="text-[11px] font-mono text-slate-500 mb-1">Fill Level</p>
                    <Input data-testid="calib-level-input" type="number" min="0" value={calibLevel} onChange={(e) => setCalibLevel(e.target.value)} className={inputCls} />
                    <span className="absolute right-3 top-[34px] text-xs font-mono text-slate-500 pointer-events-none">cm</span>
                  </div>
                ) : (
                  <div className="relative mt-2">
                    <p className="text-[11px] font-mono text-slate-500 mb-1">Volume</p>
                    <Input data-testid="calib-volume-input" type="number" min="0" value={calibVolume} onChange={(e) => setCalibVolume(e.target.value)} className={inputCls} />
                    <span className="absolute right-3 top-[34px] text-xs font-mono text-slate-500 pointer-events-none">L</span>
                  </div>
                )}
                <p className="mt-2 text-xs font-mono text-cyan-400" data-testid="calib-interp-result">
                  {calibReadMode === "level"
                    ? `≈ ${fmt(calibLiqL)} L at ${fmt(calibLevelVal, 1)} cm`
                    : `≈ ${fmt(calibLevelVal, 1)} cm at ${fmt(calibLiqL)} L`}
                </p>
              </div>
              )
            ) : isCompartments ? null : (
              <div>
                <div className="flex items-center justify-between">
                  <p className={labelCls}>Liquid Fill Level</p>
                  <span className="text-xs font-mono text-amber-400" data-testid="fill-pct-badge">{fmt(fillPct, 1)}%</span>
                </div>
                <div className="relative mt-2">
                  <Input data-testid="fill-level-input" type="number" min="0" value={fill} onChange={(e) => setFill(e.target.value)} className={inputCls} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-500 pointer-events-none">{unit}</span>
                </div>
                <Slider
                  data-testid="fill-slider"
                  className="mt-4"
                  value={[fillPct]}
                  max={100}
                  step={0.5}
                  onValueChange={([p]) => {
                    if (!geom) return;
                    setFill(String(+(((p / 100) * geom.maxFillCm) / UNIT_TO_CM[unit]).toFixed(3)));
                  }}
                />
                {fillExceeds && (
                  <p data-testid="fill-overflow-warning" className="text-xs text-red-400 mt-2 font-mono">Fill level exceeds tank height — clamped to maximum.</p>
                )}
              </div>
            )}

            {!isCompartments && (
            <div>
              <p className={labelCls}>Liquid Type</p>
              <Select value={liquidName} onValueChange={setLiquidName}>
                <SelectTrigger data-testid="liquid-preset-select" className="mt-2 bg-[#0D131E] border-[#243046] font-mono text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131924] border-[#243046] text-slate-200">
                  {LIQUIDS.map((l) => (
                    <SelectItem key={l.name} value={l.name} data-testid={`liquid-option-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                      {l.name === "Mixture" ? "Mixture — blend 2 liquids" : `${l.name} — ${l.density} kg/L`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {liquidName === "Custom" && (
                <div className="relative mt-2">
                  <Input data-testid="custom-density-input" type="number" step="0.001" min="0" value={customDensity} onChange={(e) => setCustomDensity(e.target.value)} className={inputCls} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-500 pointer-events-none">kg/L</span>
                </div>
              )}
              {isMixture && (
                <div className="mt-3 space-y-3 rounded-lg border border-violet-500/30 bg-[#0D131E] p-3" data-testid="mixture-panel">
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "Liquid A", value: mixA, set: setMixA, testid: "mix-liquid-a-select", optPrefix: "mix-a-option" },
                      { label: "Liquid B", value: mixB, set: setMixB, testid: "mix-liquid-b-select", optPrefix: "mix-b-option" },
                    ].map((side) => (
                      <div key={side.label}>
                        <p className="text-[11px] font-mono text-slate-500 mb-1">{side.label}</p>
                        <Select value={side.value} onValueChange={side.set}>
                          <SelectTrigger data-testid={side.testid} className="bg-[#131924] border-[#243046] font-mono text-slate-200 text-xs h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#131924] border-[#243046] text-slate-200">
                            {mixables.map((l) => (
                              <SelectItem key={l.name} value={l.name} data-testid={`${side.optPrefix}-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                {l.name} — {l.density} kg/L
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-mono text-slate-500">% of {mixALiq.name}</p>
                      <span className="text-xs font-mono text-violet-300" data-testid="mix-percent-badge">{pctClamped}%</span>
                    </div>
                    <Slider data-testid="mix-percent-slider" value={[pctClamped]} max={100} step={1} onValueChange={([p]) => setMixPct(String(p))} className="mt-2" />
                    <div className="relative mt-2">
                      <Input data-testid="mix-percent-input" type="number" min="0" max="100" value={mixPct} onChange={(e) => setMixPct(e.target.value)} className={inputCls} />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-500 pointer-events-none">% A</span>
                    </div>
                  </div>
                  <p className="text-xs font-mono text-violet-300" data-testid="mixture-density-badge">
                    ρ mix = {fmt(density, 3)} kg/L · {pctClamped}% {mixALiq.name} + {100 - pctClamped}% {mixBLiq.name}
                  </p>
                  <p className="text-[10px] font-mono text-slate-500">Volume-additive blend estimate — actual density may vary with temperature.</p>
                </div>
              )}
            </div>
            )}
          </section>

          {/* Display panel */}
          <section className={`${isMobileView ? "" : "lg:col-span-7 "}space-y-6`}>
            <div className="bg-[#131924] rounded-xl border border-[#243046] p-6 shadow-2xl rise rise-1" data-testid="diagram-card">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold font-mono text-slate-200">Live Tank Diagram</h2>
                <span className="text-[11px] text-slate-500 font-mono">{isCalibration ? "linear interpolation from calibration data" : "drag tank: fill level · amber handles: resize"}</span>
              </div>
              {isCalibration ? (
                isCompartments ? (
                  compStats.length ? (
                    <CompartmentDiagram shape="rectangular" wCm={calibMaxLevel || 100} hCm={calibMaxLevel || 100} compStats={compStats} labels={{ ...labels, len: "" }} showEndView={false} />
                  ) : (
                    <p data-testid="diagram-placeholder" className="text-sm text-slate-500 text-center py-24 font-mono">Add at least 2 calibration points to render the gauge.</p>
                  )
                ) : calibPoints.length >= 2 ? (
                  <CalibrationGauge points={calibPoints} frac={frac} levelVal={calibLevelVal} liters={calibLiqL} capacity={calibCapacityL} color={liquidColor} />
                ) : (
                  <p data-testid="diagram-placeholder" className="text-sm text-slate-500 text-center py-24 font-mono">Add at least 2 calibration points to render the gauge.</p>
                )
              ) : isCompartments && geom ? (
                <CompartmentDiagram shape={shape} wCm={geom.wCm} hCm={geom.hCm} compStats={compStats} labels={labels} />
              ) : geom ? (
                <TankDiagram
                  orientation={orientation}
                  shape={shape}
                  wCm={geom.wCm}
                  hCm={geom.hCm}
                  lengthCm={geom.lengthCm}
                  frac={frac}
                  color={liquidColor}
                  overflow={overflow}
                  labels={labels}
                  onFillFrac={(f) => setFill(String(+((f * geom.maxFillCm) / UNIT_TO_CM[unit]).toFixed(3)))}
                  onResizeDim={handleResizeDim}
                />
              ) : (
                <p data-testid="diagram-placeholder" className="text-sm text-slate-500 text-center py-24 font-mono">Enter valid tank dimensions to render the diagram.</p>
              )}
            </div>

            <div className={isMobileView ? "grid grid-cols-2 gap-4 rise rise-2" : "grid grid-cols-2 md:grid-cols-3 gap-4 rise rise-2"} data-testid="results-grid">
              <MetricCard label="Liquid Volume" value={fmt(liqL)} unitLabel="L" testid="liquid-volume-liters" big span={isMobileView ? "" : "md:col-span-2"} />
              <MetricCard label="Fill Percentage" value={fmt(fillPct, 1)} unitLabel="%" testid="fill-percentage" />
              <MetricCard label="Total Capacity" value={fmt(capL)} unitLabel="L" testid="total-capacity-liters" />
              <MetricCard label="Ullage / Free Space" value={fmt(ullageL)} unitLabel="L" testid="ullage-liters" />
              <MetricCard label="Estimated Weight" value={`${fmt(weightKg, 1)}`} unitLabel={`kg · ${fmt(weightKg * 2.20462, 1)} lbs`} testid="estimated-weight" />
            </div>

            {isCompartments && compStats.length > 0 && (
              <div className="bg-[#131924] rounded-xl border border-[#243046] p-5 shadow-xl rise rise-2" data-testid="compartments-card">
                <p className={labelCls}>Compartment Breakdown</p>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs font-mono" data-testid="compartments-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#243046]">
                        <th className="py-1.5 pr-2">Comp</th>
                        <th className="py-1.5 pr-2">Liquid</th>
                        <th className="py-1.5 pr-2">Fill</th>
                        <th className="py-1.5 pr-2">Volume</th>
                        <th className="py-1.5">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compStats.map((c, i) => (
                        <tr key={i} className="border-b border-[#243046]/60" data-testid={`comp-stat-row-${i}`}>
                          <td className="py-2 pr-2 text-amber-300">C{i + 1}</td>
                          <td className="py-2 pr-2">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                              {c.liquid}
                            </span>
                          </td>
                          <td className={`py-2 pr-2 ${c.frac >= 0.95 ? "text-red-400" : "text-slate-200"}`}>{c.fill} {unit} ({Math.round(c.frac * 100)}%)</td>
                          <td className="py-2 pr-2 text-slate-200" data-testid={`comp-volume-${i}`}>{fmt(c.liters)} L</td>
                          <td className="py-2 text-slate-400">{fmt(c.weight, 1)} kg</td>
                        </tr>
                      ))}
                      <tr className="text-slate-100 font-semibold" data-testid="comp-total-row">
                        <td className="py-2 pr-2" colSpan="3">Total</td>
                        <td className="py-2 pr-2 text-amber-400">{fmt(compLiqL)} L</td>
                        <td className="py-2 text-slate-300">{fmt(compWeightKg, 1)} kg</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="bg-[#131924] rounded-xl border border-[#243046] p-5 shadow-xl rise rise-2" data-testid="conversions-card">
              <p className={labelCls}>Equivalent Units</p>
              <div className={isMobileView ? "grid grid-cols-2 gap-4 mt-3" : "grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3"}>
                {LITER_CONVERSIONS.map((c) => (
                  <div key={c.key}>
                    <p className="text-[11px] font-mono text-slate-500">{c.label}</p>
                    <p className="text-lg font-bold font-mono text-cyan-400" data-testid={`conversion-${c.key}`}>{fmt(liqL * c.perLiter, 3)}</p>
                  </div>
                ))}
              </div>
            </div>

            <MeasurementLog rows={logRows} tankName={tankName} setTankName={setTankName} onAdd={addLogRow} onClear={() => setLogRows([])} onDeleteRow={deleteLogRow} highlightId={logHighlightId} isMobileView={isMobileView} />

            <div className="flex flex-wrap gap-3 rise rise-3">
              <Button data-testid="copy-summary-btn" onClick={handleCopy} className="bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">
                <Copy />Copy Summary
              </Button>
              <Button data-testid="strapping-table-btn" variant="outline" onClick={() => setStrapOpen(true)} className="border-[#243046] bg-transparent text-slate-200 hover:bg-[#1A2232] hover:text-slate-100">
                <Table2 />Strapping Table
              </Button>
              <Button data-testid="reset-btn" variant="outline" onClick={handleReset} className="border-[#243046] bg-transparent text-slate-200 hover:bg-[#1A2232] hover:text-slate-100">
                <RotateCcw />Reset
              </Button>
            </div>
            <StrappingTable open={strapOpen} onOpenChange={setStrapOpen} rows={strapRows} unit={unit} meta={meta} />
          </section>
        </div>

        <AdminPanel open={adminOpen} onOpenChange={setAdminOpen} currentUserId={user?.id} />

        <footer className="mt-10 border-t border-[#243046] pt-6 pb-2 text-xs text-slate-500 font-mono text-center" data-testid="app-footer">
          <div className="flex items-center justify-center gap-6 mb-4">
            <img src="/logos/opus-chrome.webp" alt="PT. Opus Petroindo logo" data-testid="footer-logo-opus" className="h-12" />
            <span className="h-8 w-px bg-[#243046]" />
            <img src="/logos/cjms-chrome.webp" alt="PT. Citra Jaya Makmur Sejahtera logo" data-testid="footer-logo-cjms" className="h-12" />
          </div>
          <p>PT. Opus Petroindo - PT. Citra Jaya Makmur Sejahtera</p>
        </footer>
      </main>
    </div>
  );
}

export default App;
